import { ParamNames } from '../../store/types';
import { SDKError } from '../../ErrorModule';
/**
 * Validates and processes properties for the Monochrome variant.
 * Ensures only allowed properties ('threshold', 'hexColor', 'invertColor') are present.
 * Throws an error for invalid properties or if 'threshold' is out of range (0 to 1).
 * Removes the leading '#' symbol from 'hexColor' if present.
 */
function checkMonochromeParams(properties) {
    const allowedProperties = ['threshold', 'hexColor', 'invertColor'];
    // Check if any invalid property is passed
    for (const key of Object.keys(properties)) {
        if (!allowedProperties.includes(key)) {
            throw new SDKError(`Invalid property: ${key} for Monochrome params`);
        }
    }
    if ('threshold' in properties && properties.threshold !== undefined && properties.threshold !== null) {
        if (properties.threshold < 0 || properties.threshold > 1) {
            throw new SDKError('Invalid threshold value. It must be a number between 0 and 1.');
        }
    }
    // remove the first hash from the hexColor, if #FF0000 we must pass only FF0000 to the URL
    if ('hexColor' in properties && properties.hexColor) {
        // eslint-disable-next-line no-param-reassign
        properties.hexColor = properties.hexColor.replace(/^#/, '');
    }
    return true;
}
function checkEmbroideryParam(properties) {
    const allowedProperties = ['processBackgroundColor'];
    // Check if any invalid property is passed
    for (const key of Object.keys(properties)) {
        if (!allowedProperties.includes(key)) {
            throw new SDKError(`Invalid property: ${key} for embroidery params`);
        }
    }
    return true;
}
/**
 * Validates and processes properties for the Saliency Map variant.
 * Ensures only allowed properties ('saliencyMap', 'saliencyMode', 'requiredWidth', 'requiredHeight') are present.
 * Throws an error for invalid properties or if 'requiredWidth' and 'requiredHeight' are negative.
 */
function checkSaliencyMapParams(properties) {
    const allowedProperties = ['saliencyMap', 'saliencyMode', 'requiredWidth', 'requiredHeight'];
    // Check if any invalid property is passed
    for (const key of Object.keys(properties)) {
        if (!allowedProperties.includes(key)) {
            throw new SDKError(`Invalid property: ${key} for Saliency Map params`);
        }
    }
    if ('requiredWidth' in properties && properties.requiredWidth !== undefined && properties.requiredWidth !== null) {
        if (properties.requiredWidth <= 0) {
            throw new SDKError('Invalid requiredWidth value. It must be greater than 0');
        }
    }
    if ('requiredHeight' in properties &&
        properties.requiredHeight !== undefined &&
        properties.requiredHeight !== null) {
        if (properties.requiredHeight <= 0) {
            throw new SDKError('Invalid requiredHeight value. It must be greater than 0');
        }
    }
    return true;
}
/**
 * Validates and processes properties for the Cropped variant.
 * Ensures only allowed properties ('bottom', 'top', 'right', 'left', 'cropMode') are present.
 * Throws an error for missing required properties.
 * Validates that 'left', 'right', 'top', and 'bottom' are numbers between 0 and 1 (excluding 1).
 * Ensures that 'left + right < 1' and 'top + bottom < 1' to maintain valid cropping.
 */
function checkCroppedParams(properties) {
    const allowedProperties = ['bottom', 'top', 'right', 'left', 'cropMode'];
    // Check if any invalid property is passed
    for (const key of Object.keys(properties)) {
        if (!allowedProperties.includes(key)) {
            throw new SDKError(`Invalid property: ${key} for Cropped params`);
        }
    }
    // Check if all required parameters are present and validate their values for croppedParams
    if ('cropMode' in properties) {
        // Narrowing down our scope to croppedParams
        // checking if all the required values are present
        for (const prop of allowedProperties) {
            if (!(prop in properties)) {
                throw new SDKError(`Missing required property for cropped params: ${prop}`);
            }
            // Validate values for left, right, top, bottom
            if (['left', 'right', 'top', 'bottom'].includes(prop)) {
                const value = properties[prop];
                // Ensure the value is a number between 0 and 1 (exclusive)
                if (typeof value !== 'number' || value < 0 || value >= 1) {
                    throw new SDKError(`Invalid ${prop} value. It must be a number between 0 and 1 (excluding 1).`);
                }
            }
        }
        // Check if left + right < 1 and top + bottom < 1
        const { left } = properties;
        const { right } = properties;
        const { top } = properties;
        const { bottom } = properties;
        if (left + right >= 1) {
            throw new SDKError('Invalid sum of left and right. It must be less than 1.');
        }
        if (top + bottom >= 1) {
            throw new SDKError('Invalid sum of top and bottom. It must be less than 1.');
        }
    }
    else {
        throw new SDKError('Invalid type: properties should be of type croppedParams.');
    }
    return true;
}
/**
 * Validates and processes properties for the Background Removed variant.
 * Ensures only allowed properties are present.
 * Throws an error if an invalid property is found.
 */
function checkBackgroundRemovedParams(properties) {
    const allowedProperties = [
        'newBackgroundUrl',
        'enhanceResolution',
        'tryFillHoles',
        'preserveTexture',
        'optimizeSaliencyMask',
        'backgroundDetectMode',
        'classificationType',
    ];
    // Check if any invalid property is passed
    for (const key of Object.keys(properties)) {
        if (!allowedProperties.includes(key)) {
            throw new SDKError(`Invalid property: ${key} for Background Removed params`);
        }
    }
    if ('newBackgroundUrl' in properties && properties.newBackgroundUrl) {
        // eslint-disable-next-line no-param-reassign
        properties.newBackgroundUrl = encodeURIComponent(properties.newBackgroundUrl);
    }
    return true;
}
/**
 * Validates and processes properties for the color reduce variant.
 * Ensures only allowed properties are present.
 * Throws an error if an invalid property is found.
 * Validates if noOfColors field is in valid range. i.e. (1 <= noOfColors <= 20)
 */
function checkColorReduceParams(properties) {
    const allowedProperties = ['noOfColors', 'output'];
    // Check if any invalid property is passed
    for (const key of Object.keys(properties)) {
        if (!allowedProperties.includes(key)) {
            throw new SDKError(`Invalid property: ${key} for ColorReduce params`);
        }
    }
    if ('noOfColors' in properties && properties.noOfColors !== undefined && properties.noOfColors !== null) {
        if (properties.noOfColors < 1 || properties.noOfColors > 20) {
            throw new SDKError('Invalid noOfColors value. It must be between 1 and 20');
        }
    }
    if ('output' in properties && properties.output) {
        if (properties.output !== 'documentItem') {
            throw new SDKError('Invalid output param value for colorReduce. It should be documentItem');
        }
    }
    return true;
}
/**
 * Validates and processes properties for the Segmentize variant.
 * Ensures only allowed properties are present.
 * Throws an error if an invalid property is found.
 */
function checkSegmentizeParams(properties) {
    const allowedProperties = ['output'];
    // Check if any invalid property is passed
    for (const key of Object.keys(properties)) {
        if (!allowedProperties.includes(key)) {
            throw new SDKError(`Invalid property: ${key} for Segmentize params`);
        }
        if ('output' in properties && properties.output) {
            if (properties.output !== 'svg') {
                throw new SDKError('Invalid output param value for Segmentize. It should be svg');
            }
        }
    }
    return true;
}
/**
 * Determines which validation function to run based on the variant name.
 * Calls the appropriate function to validate and process properties.
 * Returns true if the properties are valid.
 */
function checkVariantProperties(variantName, properties) {
    switch (variantName) {
        case 'monochrome': {
            return checkMonochromeParams(properties);
        }
        case 'saliencyMap': {
            return checkSaliencyMapParams(properties);
        }
        case 'cropped': {
            return checkCroppedParams(properties);
        }
        case 'backgroundRemoved': {
            return checkBackgroundRemovedParams(properties);
        }
        case 'colorReduce': {
            return checkColorReduceParams(properties);
        }
        case 'segmentize': {
            return checkSegmentizeParams(properties);
        }
        case 'embroidery': {
            return checkEmbroideryParam(properties);
        }
        default: {
            return true;
        }
    }
}
// This function can be used to get the stacked varinat URL, variant and properties specified in the query array
// will be concated with the url provided in the input parameter and the stacked variant URL will be returned.
export function getStackedVariantUrlForQueryParams(query, url) {
    let baseUrl = url;
    query.forEach((variant) => {
        baseUrl = `${baseUrl}/${variant.variantName}`;
        const { properties } = variant;
        if (properties && checkVariantProperties(variant.variantName, properties)) {
            const queryParams = [];
            Object.entries(properties).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    // Retrieve the shorthand abbreviation from ParamNames enum
                    const shorthand = ParamNames[key];
                    if (shorthand) {
                        const paramString = `${shorthand}=${value}`;
                        queryParams.push(paramString);
                    }
                    else {
                        throw new SDKError(`Invalid Property provided, property ${key}`);
                    }
                }
                else {
                    throw new SDKError(`Invalid parameters provided for key: ${key}`);
                }
            });
            if (queryParams.length > 0) {
                baseUrl += `:${queryParams.join(',')}`;
            }
        }
    });
    return baseUrl;
}
//# sourceMappingURL=getStackedVariantUrlForQueryParams.js.map